import React, { FunctionComponent, useEffect, useState } from "react";

import MaterialTable from "@material-table/core";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { listUsers } from "../../../api/segmentApi";
import { isOk } from "../../../result";
import { ExportCsv, ExportPdf } from "@material-table/exporters";
import { AccountCircle } from "@material-ui/icons";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import useStyles from "./style";

import * as SegmentApi from "../../../api/segmentApi";
import { Alert } from "@material-ui/lab";
import { useSelector } from "react-redux";
import { AppState } from "../../../store";
import { UserState } from "../../../store/users/types";
import { thunkFetchUsers } from "../../../store/users/actions";
import { useLoadIfEmpty } from "../../../hooks/useLoadIfEmpty";
import { theme } from "../../stores/StoreList/components/styles";
import { useI18n } from "../../../hooks/useI18n";

export interface SegmentUserListProps {
  segmentId: string;
  hasRules?: boolean;
  onSave: () => void;
}

export const SegmentUserList: FunctionComponent<SegmentUserListProps> = ({
  segmentId,
  hasRules,
  onSave
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { t } = useI18n();

  const [state, setState] = useState<any>({
    loading: false,
    loadList: true,
    users: [] as any
  });

  const { users, isLoading } =
    useSelector<AppState, UserState>(s => s.users) || [];

  useLoadIfEmpty(!users?.length, thunkFetchUsers);

  useEffect(() => {
    if (state?.loadList) {
      setState({ ...state, loadList: false, loading: true });
      listUsers(segmentId).then(result => {
        if (isOk(result)) {
          return setState({
            users: result.value,
            loading: false,
            loadList: false
          });
        }
        setState({ users: [], loading: false });
      });
    }
  }, [state]);

  return (
    <>
      <MaterialTable
        title=""
        columns={[
          {
            title: "",
            field: "id",
            searchable: true,
            width: 20,
            render: (rowData: any) => (
              <Tooltip title={"Open user profile in new window"}>
                <Link href={`users/${rowData.id}`} target={"_blank"}>
                  <AccountCircle />
                </Link>
              </Tooltip>
            )
          },
          {
            title: "FirstName",
            field: "firstName",
            hidden: true,
            export: true
          },
          {
            title: "LastName",
            field: "lastName",
            hidden: true,
            export: true
          },
          {
            title: "Name",
            export: false,
            field: "name",
            render: (rowData: any) => (
              <>
                {rowData.firstName} {rowData.lastName}
              </>
            ),
            hidden: mobile
          },
          { title: "Email", field: "email" },
          { title: "Address", field: "address", hidden: mobile },
          { title: "Zipcode", field: "zipCode", hidden: mobile },
          { title: "City", field: "city", hidden: mobile }
        ]}
        isLoading={state.loading}
        data={!state.loading ? state.users : []}
        options={{
          pageSize: 20,
          pageSizeOptions: [10, 20, 50, 100],
          exportAllData: true,
          emptyRowsWhenPaging: false,
          exportMenu: [
            {
              label: t("common.export.exportPdf"),
              exportFunc: (cols, data) =>
                ExportPdf(cols, data, `SegmentUsers_${segmentId}`)
            },
            {
              label: t("common.export.exportCsv"),
              exportFunc: (cols, data) =>
                ExportCsv(cols, data, `SegmentUsers_${segmentId}`)
            }
          ]
        }}
        actions={
          hasRules
            ? []
            : [
                {
                  icon: "add_circle",
                  iconProps: {
                    style: {
                      color: theme.palette.primary.main,
                      fontSize: "2rem"
                    }
                  },
                  tooltip: "Add new user to the segment",
                  isFreeAction: true,
                  onClick: () => {
                    setState({ ...state, addUserDialogVisible: true });
                  }
                }
              ]
        }
      />
      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"md"}
        open={state.addUserDialogVisible}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                addUserDialogVisible: false,
                addingUser: false
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        {state?.addingUser ? (
          <DialogContent
            style={
              mobile
                ? { padding: 0, marginTop: 15, marginBottom: 20 }
                : { marginTop: 15, marginBottom: 30 }
            }
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%"
              }}
            >
              <CircularProgress />
            </div>
          </DialogContent>
        ) : (
          <DialogContent
            style={
              mobile
                ? { padding: 0, marginTop: 15, marginBottom: 20 }
                : { marginTop: 15, marginBottom: 30 }
            }
          >
            <MaterialTable
              title=""
              isLoading={isLoading}
              columns={[
                {
                  title: "Id",
                  field: "id",
                  hidden: true,
                  searchable: true
                },
                {
                  title: "User",
                  field: "name",
                  render: (user: any) => (
                    <div style={{ minWidth: 180 }}>
                      {`${user.firstName ?? "..."} ${user.lastName ?? "..."}`}
                      <Typography color="textSecondary">
                        {user.email}
                      </Typography>
                    </div>
                  )
                },
                {
                  title: "Email",
                  field: "email",
                  searchable: true
                },
                {
                  title: "Address",
                  field: "address",
                  export: false,
                  searchable: true,
                  render: (user: any) => (
                    <div>
                      {user.address && user.zipCode && user.city
                        ? `${user.address}, ${user.zipCode} ${user.city}`
                        : "..."}
                    </div>
                  )
                }
              ]}
              data={users.filter(u => !state?.users.includes(u.id)) as []}
              actions={[
                {
                  icon: "add_circle",
                  iconProps: { style: { color: theme.palette.primary.main } },
                  tooltip: "Add new user to the segment",
                  onClick: async (event, rowData) => {
                    setState({ ...state, addingUser: true });
                    const response = await SegmentApi.addUser(
                      segmentId,
                      rowData.email
                    );

                    if (response.type === "OK") {
                      setState({
                        ...state,
                        addingUser: false,
                        addUserDialogVisible: false,
                        loadList: true
                      });

                      onSave();
                    } else {
                      setState({
                        ...state,
                        addingUser: false,
                        addUserError: response.details.detail
                          ? response.details.detail
                          : response.details.title
                      });
                    }
                  }
                }
              ]}
              options={{
                pageSize: 20,
                actionsColumnIndex: -1,
                actionsCellStyle: { whiteSpace: "nowrap" }
              }}
              localization={{
                header: {
                  actions: ""
                }
              }}
            />
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};
